/**
 * Practice leave: the times the whole practice takes no bookings - a weekday lunch break, a public holiday, a
 * Christmas closure. A booking inside it from the calendar is refused unless the person booking chooses "Book
 * anyway", which is audited. Only Practice Admins can change the leave; everyone else sees it read-only.
 *
 * Two kinds, on two cards: recurring breaks repeat every week on chosen days, and closures cover a date range,
 * either all day or for the same window on each day of the range. Both are edited in place and saved together.
 *
 * Public holidays close the practice too, without being entered here - CatTrax works them out from the
 * practice's timezone. The closures card lists the next year of them, each with a switch to stay open on that
 * holiday every year, saved as a PUBLIC_HOLIDAY_OPEN entry against the holiday's key.
 */
app.component("practiceLeaveSetting", {
	templateUrl: "app/src/js/components/practiceLeaveSetting/practiceLeaveSetting.html",
	controller: "practiceLeaveSettingController",
	bindings: { practiceId: "<" },
});

app.controller("practiceLeaveSettingController", [
	"$scope",
	"PracticeService",
	"ExceptionAPI",
	"Session",
	"$modal",
	"TIMEZONE_TYPE",
	function ($scope, PracticeService, ExceptionAPI, Session, $modal, TIMEZONE_TYPE) {

		$scope.API = PracticeService;
		$scope.breaks = [];
		$scope.closures = [];
		$scope.publicHolidays = [];
		$scope.holidayLoadError = null;
		$scope.isLoading = false;
		$scope.loadError = null;
		$scope.saveStatus = "";
		$scope.canEdit = false;

		$scope.DAYS = [
			{ value: "MONDAY", label: "Mon" },
			{ value: "TUESDAY", label: "Tue" },
			{ value: "WEDNESDAY", label: "Wed" },
			{ value: "THURSDAY", label: "Thu" },
			{ value: "FRIDAY", label: "Fri" },
			{ value: "SATURDAY", label: "Sat" },
			{ value: "SUNDAY", label: "Sun" },
		];

		$scope.newBreak = blankBreak();
		$scope.newClosure = blankClosure();

		function blankBreak() {
			return { name: "", days: [], start_time: null, end_time: null };
		}

		function blankClosure() {
			return { name: "", start_date: null, end_date: null, all_day: true, start_time: null, end_time: null };
		}

		this.$onInit = function () {
			// The settings dialog can be pointed at any practice the user belongs to, so every call names it.
			$scope.practiceId = this.practiceId || (Session.practice && Session.practice.id);
			const practice = (Session.practiceOptions || []).find(option => option.id === $scope.practiceId)
				|| Session.practice || {};
			$scope.practiceName = practice.name || "";
			$scope.canEdit = Session.hasPermissionAt(practice, "SCHEDULE_PRACTICE_LEAVE_MANAGE");
			const timezone = TIMEZONE_TYPE.find(zone => zone.value === practice.timezone);
			// The same fallback the calendar uses for a practice with no timezone set.
			$scope.practiceTimezone = timezone ? timezone.name : "UTC";

			loadLeave();
			loadPublicHolidays();
			loadCategories();
		};

		// The next year of public holidays. Without them the screen still works; it just cannot list them.
		function loadPublicHolidays() {
			const today = moment.tz($scope.practiceTimezone);
			$scope.API.queryPublicHolidays($scope.practiceId, today.format("YYYY-MM-DD"), today.clone().add(364, "days").format("YYYY-MM-DD"))
				.then(function (response) {
					$scope.publicHolidays = (response.data || []).map(function (holiday) {
						return {
							key: holiday.key,
							name: holiday.name,
							label: moment(holiday.date, "YYYY-MM-DD").format("ddd D MMM YYYY"),
						};
					});
				})
				.catch(function () {
					$scope.holidayLoadError = "Public holidays could not be loaded, so they are not listed here. They still close the practice.";
				});
		}

		function loadLeave() {
			$scope.isLoading = true;
			$scope.loadError = null;

			$scope.API.queryPracticeLeave($scope.practiceId)
				.then(function (response) {
					showLeave(response.data || []);
				})
				.catch(function (error) {
					$scope.loadError = ExceptionAPI.serverMessage(error)
						|| "Practice leave could not be loaded. Close settings and try again.";
				})
				.finally(function () {
					$scope.isLoading = false;
				});
		}

		// The appointment types, to name and colour the appointments the save warning lists.
		let categories = {};

		function loadCategories() {
			$scope.API.queryCalendarSetting($scope.practiceId).then(function (response) {
				categories = {};
				((response.data && response.data.appointment_categories) || []).forEach(function (category) {
					categories[category.category_id] = category;
				});
			}, angular.noop);
		}

		// --- Moving between the API's shape and the form's ---

		// Time and date inputs bind to Date objects, while the API speaks the practice's own wall clock as text.
		function toTime(text) {
			if (!text) {
				return null;
			}
			const parts = text.split(":");
			return new Date(1970, 0, 1, Number(parts[0]), Number(parts[1]), 0);
		}

		function fromTime(date) {
			return date ? moment(date).format("HH:mm") : null;
		}

		function toDate(text) {
			return text ? moment(text, "YYYY-MM-DD").toDate() : null;
		}

		function fromDate(date) {
			return date ? moment(date).format("YYYY-MM-DD") : null;
		}

		function showLeave(leave) {
			$scope.breaks = leave.filter(item => item.repeat_type === "WEEKLY").map(function (item) {
				return {
					leave_id: item.leave_id,
					name: item.name,
					days: item.days || [],
					start_time: toTime(item.start_time_local),
					end_time: toTime(item.end_time_local),
				};
			}).sort(byStartTime);

			$scope.closures = leave.filter(item => item.repeat_type === "ONE_OFF").map(function (item) {
				return {
					leave_id: item.leave_id,
					name: item.name,
					start_date: toDate(item.start_date),
					end_date: toDate(item.end_date),
					all_day: !!item.all_day,
					start_time: toTime(item.start_time_local),
					end_time: toTime(item.end_time_local),
				};
			}).sort(byStartDate);

			openOn = {};
			leave.filter(item => item.repeat_type === "PUBLIC_HOLIDAY_OPEN").forEach(function (item) {
				openOn[item.holiday_key] = { leave_id: item.leave_id, name: item.name };
			});
			savedOpenOn = angular.copy(openOn);

			rememberSaved();
		}

		function byStartTime(a, b) {
			return (fromTime(a.start_time) || "").localeCompare(fromTime(b.start_time) || "");
		}

		function byStartDate(a, b) {
			return (fromDate(a.start_date) || "").localeCompare(fromDate(b.start_date) || "");
		}

		function breakRequest(item) {
			return {
				leave_id: item.leave_id,
				name: (item.name || "").trim(),
				repeat_type: "WEEKLY",
				all_day: false,
				start_time_local: fromTime(item.start_time),
				end_time_local: fromTime(item.end_time),
				// Kept in week order whatever order they were ticked in.
				days: $scope.DAYS.map(day => day.value).filter(day => item.days.indexOf(day) !== -1),
			};
		}

		function closureRequest(item) {
			return {
				leave_id: item.leave_id,
				name: (item.name || "").trim(),
				repeat_type: "ONE_OFF",
				start_date: fromDate(item.start_date),
				end_date: fromDate(item.end_date),
				all_day: !!item.all_day,
				start_time_local: item.all_day ? null : fromTime(item.start_time),
				end_time_local: item.all_day ? null : fromTime(item.end_time),
			};
		}

		// Every holiday the practice stays open on - including ones beyond the year listed, so they are kept.
		function openOnRequests() {
			return Object.keys(openOn).sort().map(function (key) {
				return {
					leave_id: openOn[key].leave_id,
					name: (openOn[key].name || key).substring(0, 50),
					repeat_type: "PUBLIC_HOLIDAY_OPEN",
					holiday_key: key,
				};
			});
		}

		function request() {
			return $scope.breaks.map(breakRequest).concat($scope.closures.map(closureRequest), openOnRequests());
		}

		// --- What has changed ---

		let saved = "";

		function rememberSaved() {
			saved = angular.toJson(request());
		}

		$scope.hasUnsavedChanges = function () {
			return angular.toJson(request()) !== saved;
		};

		// Drops a stale "saved" tick the moment something else is edited.
		$scope.$watch($scope.hasUnsavedChanges, function (dirty) {
			if (dirty) {
				$scope.saveStatus = "";
			}
		});

		// --- What is valid ---

		function timesValid(item) {
			return !!item.start_time && !!item.end_time && fromTime(item.end_time) > fromTime(item.start_time);
		}

		// Marks the to time when both times are in but the window ends before it starts.
		$scope.isTimeRangeInvalid = function (item) {
			return !!item.start_time && !!item.end_time && !timesValid(item);
		};

		$scope.isBreakValid = function (item) {
			return !!(item.name || "").trim() && item.days.length > 0 && timesValid(item);
		};

		$scope.isClosureValid = function (item) {
			const datesValid = !!item.start_date && !!item.end_date && fromDate(item.end_date) >= fromDate(item.start_date);
			return !!(item.name || "").trim() && datesValid && (item.all_day || timesValid(item));
		};

		$scope.canSave = function () {
			return $scope.canEdit
				&& $scope.hasUnsavedChanges()
				&& $scope.breaks.every($scope.isBreakValid)
				&& $scope.closures.every($scope.isClosureValid);
		};

		// A closure that has finished is kept, dimmed, so last year's holiday is there to copy the dates from.
		$scope.isPast = function (closure) {
			return !!closure.end_date && fromDate(closure.end_date) < moment().format("YYYY-MM-DD");
		};

		$scope.upcomingClosureCount = function () {
			const closedHolidays = $scope.publicHolidays.filter(holiday => !$scope.isOpenOn(holiday)).length;
			return $scope.closures.filter(closure => !$scope.isPast(closure)).length + closedHolidays;
		};

		// --- Public holidays ---

		// holiday key -> the PUBLIC_HOLIDAY_OPEN entry keeping the practice open on it, as edited and as last saved.
		let openOn = {};
		let savedOpenOn = {};

		$scope.isOpenOn = function (holiday) {
			return !!openOn[holiday.key];
		};

		// Applies to the holiday every year, not only the date listed.
		$scope.toggleHoliday = function (holiday) {
			if (!$scope.canEdit) {
				return;
			}
			if (openOn[holiday.key]) {
				delete openOn[holiday.key];
			} else {
				// Turning one back the way it was saved keeps its id, so it is not a change at all.
				openOn[holiday.key] = savedOpenOn[holiday.key] || { leave_id: null, name: holiday.name };
			}
		};

		// --- Editing ---

		$scope.toggleDay = function (item, day) {
			if (!$scope.canEdit) {
				return;
			}
			const index = item.days.indexOf(day);
			if (index === -1) {
				item.days.push(day);
			} else {
				item.days.splice(index, 1);
			}
		};

		$scope.hasDay = function (item, day) {
			return item.days.indexOf(day) !== -1;
		};

		// A closure usually covers one day, so the to date follows the from date until it is set on its own.
		$scope.onStartDateChanged = function (item) {
			if (item.start_date && (!item.end_date || fromDate(item.end_date) < fromDate(item.start_date))) {
				item.end_date = item.start_date;
			}
		};

		$scope.addBreak = function () {
			if (!$scope.isBreakValid($scope.newBreak)) {
				return;
			}
			$scope.breaks.push($scope.newBreak);
			$scope.breaks.sort(byStartTime);
			$scope.newBreak = blankBreak();
		};

		$scope.addClosure = function () {
			if (!$scope.isClosureValid($scope.newClosure)) {
				return;
			}
			$scope.closures.push($scope.newClosure);
			$scope.closures.sort(byStartDate);
			$scope.newClosure = blankClosure();
		};

		$scope.removeBreak = function (item) {
			$scope.breaks.splice($scope.breaks.indexOf(item), 1);
		};

		$scope.removeClosure = function (item) {
			$scope.closures.splice($scope.closures.indexOf(item), 1);
		};

		// --- Saving ---

		// Appointments already booked inside the new leave are left where they are, but the person saving it is
		// told which ones first, so nobody turns up to a closed practice without someone having decided to let them.
		$scope.save = function () {
			if (!$scope.canSave()) {
				return;
			}
			const leave = request();

			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";

			$scope.API.queryPracticeLeaveAffectedAppointments($scope.practiceId, leave)
				.then(function (response) {
					const affected = response.data || [];
					if (!affected.length) {
						return saveLeave(leave);
					}
					$scope.API.isProcessing = false;
					$scope.saveStatus = "";
					confirmAffected(affected, leave);
				})
				.catch(function (error) {
					$scope.API.isProcessing = false;
					$scope.saveStatus = "Error saving practice leave";
					showError(error, "Practice leave could not be saved.");
				});
		};

		function saveLeave(leave) {
			$scope.API.isProcessing = true;
			$scope.saveStatus = "Saving...";

			return $scope.API.savePracticeLeave($scope.practiceId, leave)
				.then(function (response) {
					showLeave(response.data || []);
					$scope.saveStatus = "Practice leave saved";
				})
				.catch(function (error) {
					$scope.saveStatus = "Error saving practice leave";
					showError(error, "Practice leave could not be saved.");
				})
				.finally(function () {
					$scope.API.isProcessing = false;
				});
		}

		function confirmAffected(affected, leave) {
			$scope.affectedAppointments = affected.map(function (appointment) {
				const category = categories[appointment.category_id] || {};
				return {
					when: moment.utc(appointment.start_date_utc).tz($scope.practiceTimezone).format("ddd D MMM · HH:mm"),
					clinician: appointment.clinician_name,
					type: category.name || "",
					colour: category.colour ? (category.colour.charAt(0) === "#" ? category.colour : "#" + category.colour) : null,
				};
			});
			$scope.pendingLeave = leave;
			$scope.affectedModal = $modal({
				scope: $scope,
				templateUrl: "app/src/js/components/practiceLeaveSetting/practiceLeaveAffected.tpl.html",
				show: true,
				backdrop: true,
				keyboard: false,
			});
		}

		$scope.cancelAffected = function () {
			$scope.affectedModal.hide();
			$scope.pendingLeave = null;
		};

		$scope.saveAnyway = function () {
			$scope.affectedModal.hide();
			const leave = $scope.pendingLeave;
			$scope.pendingLeave = null;
			if (leave) {
				saveLeave(leave);
			}
		};

		// The server's message is the actionable one - "'Lunch' must end after it starts" names what to fix.
		function showError(error, lead) {
			$scope.alertMessage = lead;
			$scope.alertDetail = ExceptionAPI.serverMessage(error);
			$scope.serverErrorModal = $modal({
				scope: $scope,
				templateUrl: "app/src/views/templates/modal.server-error.tpl.html",
				show: true,
				title: "CatTrax has encountered an error",
			});
		}
	},
]);
